import React, { Fragment, useEffect, useState } from 'react';
import styles from './styles.module.scss';
import classnames from 'classnames';

import {
  AppBar,
  AppBarTitle,
  AppBarContext,
  AppBarSection,
  Button,
  ButtonGroup,
  Modal,
  RadioButton,
  InputBox,
  Icons,
  Tooltip,
  Switch,
} from '@densityco/ui';
import colorVariables from '@densityco/ui/variables/colors.json';
import FormLabel from '../FormLabel';

import { DEFAULT_ENVIRONMENT, Environment, EnvironmentData } from '../../helpers/session';
import { loadCookieData, saveCookieData } from '../../helpers/cookie-data';

const EnvironmentSwitcher: React.FunctionComponent<{
  enabled: boolean;
  showButton?: boolean;
  onChange?: (data: EnvironmentData) => void;
  defaultCustomCoreHost?: string;
}> = ({
  enabled,
  showButton = false,
  onChange = () => window.location.reload(),
  defaultCustomCoreHost = 'http://localhost:8000',
}) => {
  const [initialData, setInitialData] = useState<EnvironmentData | null>(null);
  const [workingData, setWorkingData] = useState<EnvironmentData | null>(null);
  const [open, setOpen] = useState(false);

  const environments: Array<{
    type: Environment;
    name: string;
    description: string;
    color?: string;
    controls: ((conf: EnvironmentData, setConf: any) => React.ReactNode) | null;
    defaultValue: EnvironmentData | null;
  }> = [
    {
      type: Environment.PRODUCTION,
      name: 'Production',
      description: 'The environment containing real, up to date customer data.',
      color: undefined,
      controls: null,
      defaultValue: DEFAULT_ENVIRONMENT,
    },
    {
      type: Environment.STAGING,
      name: 'Staging / Lab',
      description: 'The environment containing mock data used primarily by developers for testing.',
      color: colorVariables.red,
      controls: null,
      defaultValue: {
        ...DEFAULT_ENVIRONMENT,
        type: Environment.STAGING,
        coreHost: 'https://core-staging.density.io',
      },
    },
    {
      type: Environment.LOCAL,
      name: 'Local',
      description: 'For local development against standalone servers (Core API running on port 8000).',
      color: colorVariables.yellow,
      controls: null,
      defaultValue: {
        ...DEFAULT_ENVIRONMENT,
        type: Environment.LOCAL,
        coreHost: 'http://localhost:8000',
        useAuth0: false,
      },
    },
    {
      type: Environment.KANDOR,
      name: 'Kandor',
      description: 'For local development against Kandor (Core API running on port 8081).',
      color: colorVariables.purple,
      controls: null,
      defaultValue: {
        ...DEFAULT_ENVIRONMENT,
        type: Environment.KANDOR,
        coreHost: 'http://localhost:8081',
        useAuth0: false,
      },
    },
    {
      type: Environment.CUSTOM,
      name: 'Custom',
      description: 'For development against a custom cluster.',
      color: colorVariables.green,
      controls: (selectedEnvironment: EnvironmentData, setSelectedEnvironment: (data: EnvironmentData) => void) => {
        if (selectedEnvironment.type !== Environment.CUSTOM) {
          return null;
        }
        return (
          <Fragment>
            <FormLabel
              label="Core API base URL"
              htmlFor="environment-core-host"
              input={
                <InputBox
                  value={selectedEnvironment.coreHost}
                  id="environment-core-host"
                  width="100%"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSelectedEnvironment({
                      ...selectedEnvironment,
                      coreHost: e.target.value,
                    })
                  }
                />
              }
            />
            <FormLabel
              label={
                <Tooltip
                  target={
                    <div className={styles.environmentGlobalControlsTooltipTarget}>
                      Use Auth0 login page&nbsp;
                      <Icons.Info />
                    </div>
                  }
                  contents={
                    <Fragment>
                      By default, the dashboard uses Auth0 to login. Turn this off to revert to a basic login form (for apps that
                      support it).
                    </Fragment>
                  }
                />
              }
              htmlFor="environment-use-auth0"
              input={
                <Switch
                  value={workingData ? workingData.useAuth0 : true}
                  id="environment-use-auth0"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (workingData) {
                      setWorkingData({
                        ...workingData,
                        useAuth0: e.target.checked,
                      });
                    }
                  }}
                />
              }
            />
          </Fragment>
        );
      },
      defaultValue: {
        ...DEFAULT_ENVIRONMENT,
        type: Environment.CUSTOM,
        coreHost: defaultCustomCoreHost,
      },
    },
  ];

  // Preload the initial state from the cookie
  useEffect(() => {
    const environment = loadCookieData().environment;
    setInitialData(environment);
    setWorkingData(environment);
  }, [setInitialData, setWorkingData]);

  // Listen for a special sequence of keys to open the environment switcher.
  //
  // This is to both maintain backwards compatibility with the old environment switcher, as well as
  // so that if the navbar button isn't visible, then it can still be opened.
  useEffect(() => {
    const keys = ['!', '!', '`', ' '];
    let indexInKeys = 0;

    function onKeyDown(e: KeyboardEvent) {
      if (keys[indexInKeys] === e.key) {
        if (indexInKeys === keys.length - 1) {
          indexInKeys = 0;
          setOpen(true);
        }
        indexInKeys += 1;
      } else {
        // Wasn't the right key, do nothing.
        indexInKeys = 0;
      }
      // Reset after a couple seconds
      window.setTimeout(() => {
        indexInKeys = 0;
      }, 5000);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setOpen]);

  const configurationForSelectedEnvironment = environments.find((conf) => conf.type === workingData?.type);
  const button = configurationForSelectedEnvironment ? (
    <Tooltip
      placement="bottom-end"
      contents={`Environment: ${configurationForSelectedEnvironment.type}`}
      target={
        <li className={styles.environmentDefaultIcon} onClick={() => setOpen(true)}>
          <Icons.CloudSecure color={configurationForSelectedEnvironment.color} />
        </li>
      }
    />
  ) : null;

  const buttonVisible = initialData?.showButton || initialData?.type !== Environment.PRODUCTION || showButton;

  if (!enabled) {
    return null;
  }
  if (!open) {
    return buttonVisible ? button : null;
  }

  const close = () => {
    setWorkingData(loadCookieData().environment);
    setOpen(false);
  };

  return workingData ? (
    <Fragment>
      {buttonVisible ? button : null}
      <Modal visible={open} onEscape={close} onBlur={close} width={500}>
        <AppBar>
          <AppBarTitle>Switch Environment</AppBarTitle>
        </AppBar>
        <p className={styles.environmentDescription}>
          This is a function intended for developers!&nbsp;
          <strong style={{ color: colorVariables.red }}>If you opened this by mistake, click cancel to return to the app.</strong>
        </p>
        <ul className={styles.environmentChoiceList}>
          {environments.map((environment) => (
            <div className={styles.environmentChoiceWrapper} key={environment.type}>
              <li
                className={classnames(styles.environmentChoice, {
                  [styles.selected]: workingData?.type === environment.type,
                })}
                onClick={() => setWorkingData(environment.defaultValue)}
              >
                <RadioButton checked={workingData?.type === environment.type} onChange={() => {}} />
                <div>
                  <h3>{environment.name}</h3>
                  <p>{environment.description}</p>
                </div>
              </li>
              {workingData?.type === environment.type && environment.controls !== null ? (
                <div className={styles.environmentChoiceControls}>
                  {environment.controls(workingData, (configuration: EnvironmentData) => setWorkingData(configuration))}
                </div>
              ) : null}
            </div>
          ))}
        </ul>
        <details className={styles.environmentGlobalControls}>
          <summary>More options</summary>
          <div className={styles.environmentGlobalControlsInner}>
            <FormLabel
              label={
                <Tooltip
                  target={
                    <div className={styles.environmentGlobalControlsTooltipTarget}>
                      Always show environment switcher button in navbar&nbsp;
                      <Icons.Info />
                    </div>
                  }
                  contents={
                    <Fragment>
                      By default, the environment switcher button is only hidden when connected to production. When this setting
                      is enabled, it's always shown - this can be helpful for developers who switch environments regularly.
                      <br />
                      {button}
                    </Fragment>
                  }
                />
              }
              htmlFor="environment-button-visible"
              input={
                <Switch
                  value={workingData.showButton}
                  id="environment-button-visible"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setWorkingData({
                      ...workingData,
                      showButton: e.target.checked,
                    })
                  }
                />
              }
            />
            <FormLabel
              label={
                <Tooltip
                  target={
                    <div className={styles.environmentGlobalControlsTooltipTarget}>
                      Use 'slow mode' when requesting space counts&nbsp;
                      <Icons.Info />
                    </div>
                  }
                  contents={
                    <Fragment>
                      By default, the dashboard receives cached count data from the API. When this option is turned on, the
                      additional "&slow=true" query parameter is included in all "GET /v2/spaces/:id/counts" API requests, which
                      instead forces the API to skip the cache and manually recalculate count data for every request.
                    </Fragment>
                  }
                />
              }
              htmlFor="environment-slow-mode"
              input={
                <Switch
                  value={workingData.slowMode}
                  id="environment-slow-mode"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setWorkingData({
                      ...workingData,
                      slowMode: e.target.checked,
                    })
                  }
                />
              }
            />
            <FormLabel
              label={
                <Tooltip
                  target={
                    <div className={styles.environmentGlobalControlsTooltipTarget}>
                      Use legacy 'core-v2' endpoint when requesting space counts&nbsp;
                      <Icons.Info />
                    </div>
                  }
                  contents={
                    <Fragment>
                      By default, the dashboard will receive counts from the current prod counts API. When this option is turned
                      on, it will instead request counts from e.g. GET /core-v2/spaces/:id/counts, which is where the "old v2
                      counts API" is located.
                    </Fragment>
                  }
                />
              }
              htmlFor="environment-legacy-api"
              input={
                <Switch
                  value={workingData.legacyAPI}
                  id="environment-legacy-api"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setWorkingData({
                      ...workingData,
                      legacyAPI: e.target.checked,
                    })
                  }
                />
              }
            />
          </div>
        </details>
        <AppBarContext.Provider value="BOTTOM_ACTIONS">
          <AppBar>
            <AppBarSection>
              <Button
                variant="underline"
                type="muted"
                onClick={() => {
                  const cookieData = loadCookieData();
                  cookieData.environment = DEFAULT_ENVIRONMENT;
                  saveCookieData(cookieData);

                  // Refresh to reload the app with the new hosts
                  // Delay is here to give feedback that it worked by letting the modal close
                  setTimeout(() => {
                    window.location.reload();
                  }, 500);
                }}
              >
                Reset to default
              </Button>
            </AppBarSection>
            <AppBarSection>
              <ButtonGroup>
                <Button variant="underline" onClick={close}>
                  Cancel
                </Button>
                <Button
                  variant="filled"
                  onClick={() => {
                    if (workingData) {
                      const cookieData = loadCookieData();
                      cookieData.environment = workingData;
                      saveCookieData(cookieData);
                      onChange(workingData);
                      setOpen(false);
                    }
                  }}
                >
                  Save
                </Button>
              </ButtonGroup>
            </AppBarSection>
          </AppBar>
        </AppBarContext.Provider>
      </Modal>
    </Fragment>
  ) : null;
};

EnvironmentSwitcher.displayName = 'EnvironmentSwitcher';
export default EnvironmentSwitcher;
